import axios from '@/api/axios';
import {toQueryString} from '@/api/queryString';
import type {
  AdminAfriexPerson,
  AdminPaginatedEnvelope,
  AfriexReferral,
  AfriexReferralTier,
} from '@/types';
import type {AfriexQueryParams} from '@/types/AfriexQueryParams';

export const referralService = {
  getReferralList: async (
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<AfriexReferral[]>> => {
    const params = {page: 0, limit: 10, ...filters};
    const url = `/v2/admin/referrals?${toQueryString(params)}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<AfriexReferral[]>>(url);
    return response?.data;
  },

  fulfillReferral: async (referralId: string): Promise<unknown> => {
    const response = await axios.post<unknown>(
      `/v2/admin/referrals/pay/${referralId}`,
    );
    return response.data;
  },

  blockPayout: async (referralId: string): Promise<unknown> => {
    const response = await axios.post<unknown>(
      `/v2/admin/referrals/block/${referralId}`,
    );
    return response.data;
  },
  searchAutoComplete: async (searchTerm: string): Promise<AfriexReferral[]> => {
    const params = {searchTerm: searchTerm?.trim()?.toString()};
    const url = `/v2/admin/referrals/autocomplete?${toQueryString(params)}`;
    const response = await axios.get<AfriexReferral[]>(url);
    return response.data || [];
  },
  getTierList: async (
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<AfriexReferralTier[]>> => {
    const params = {page: 0, limit: 10, ...filters};
    const url = `/v2/admin/referral-tiers?${toQueryString(params)}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<AfriexReferralTier[]>>(url);
    return response?.data;
  },
  getSignupCodeList: async (
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<AfriexReferralTier[]>> => {
    const params = {page: 0, limit: 10, ...filters, type: 'signup'};
    const url = `/v2/admin/referral-tiers?${toQueryString(params)}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<AfriexReferralTier[]>>(url);
    return response?.data;
  },
  getEventCodeList: async (
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<AfriexReferralTier[]>> => {
    const params = {page: 0, limit: 10, ...filters, type: 'event'};
    const url = `/v2/admin/referral-tiers?${toQueryString(params)}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<AfriexReferralTier[]>>(url);
    return response?.data;
  },
  getTierUserList: async (
    tierId: string,
    filters: AfriexQueryParams,
  ): Promise<AdminPaginatedEnvelope<Partial<AdminAfriexPerson>[]>> => {
    const params = {page: 0, limit: 10, ...filters};
    const url = `/v2/admin/referral-tiers/users/${tierId}?${toQueryString(
      params,
    )}`;
    const response =
      await axios.get<AdminPaginatedEnvelope<Partial<AdminAfriexPerson>[]>>(
        url,
      );
    return response?.data;
  },
  getTier: async (tierId: string): Promise<AfriexReferralTier> => {
    const url = `/v2/admin/referral-tiers/${tierId}`;
    const response = await axios.get<AfriexReferralTier>(url);
    return response?.data;
  },
  updateTier: async (
    tierId: string,
    tier: Partial<AfriexReferralTier>,
  ): Promise<AfriexReferralTier> => {
    const url = `/v2/admin/referral-tiers/${tierId}`;
    const response = await axios.patch<AfriexReferralTier>(url, tier);
    return response?.data;
  },
  createTier: async (
    tier: Partial<AfriexReferralTier>,
  ): Promise<AfriexReferralTier> => {
    const url = `/v2/admin/referral-tiers`;
    const response = await axios.post<AfriexReferralTier>(url, tier);
    return response?.data;
  },
  addUserToTier: async (
    tierId: string,
    userId: string,
  ): Promise<AfriexReferralTier> => {
    const url = `/v2/admin/referral-tiers/users/${userId}/${tierId}`;
    const response = await axios.post<AfriexReferralTier>(url);
    return response?.data;
  },

  addUsersToTier: async (
    tierId: string,
    userIds: string[],
  ): Promise<AfriexReferralTier> => {
    const url = `/v2/admin/referral-tiers/users/${tierId}`;
    const response = await axios.post<AfriexReferralTier>(url, {userIds});
    return response?.data;
  },
  removeUserFromTier: async (
    tierId: string,
    userId: string,
  ): Promise<unknown> => {
    const url = `/v2/admin/referral-tiers/users/${userId}/${tierId}`;
    const response = await axios.delete<unknown>(url);
    return response?.data;
  },
};
